import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Check } from "lucide-react";
import React, { useState } from "react";
import {
  TbAlertCircle
} from "react-icons/tb";

import { useAuth } from "@/context/AuthContext";
import { PiCellSignalFullFill, PiCellSignalLowFill, PiCellSignalMediumFill, PiCellSignalNoneFill } from "react-icons/pi";
import { toast } from 'sonner';

export function PrioritySelect({ task, showLabel = false, iconOnly = false, onPriorityChange, onTaskChange, className }) {
  const [open, setOpen] = useState(false);
  const { axiosInstance } = useAuth();

  const priorities = {
    no_priority: { name: 'No Priority', icon: PiCellSignalNoneFill, color: 'text-neutral-500' },
    low: { name: 'Low', icon: PiCellSignalLowFill, color: 'text-violet-500' },
    medium: { name: 'Medium', icon: PiCellSignalMediumFill, color: 'text-orange-500' },
    high: { name: 'High', icon: PiCellSignalFullFill, color: 'text-red-500' },
    urgent: { name: 'Urgent', icon: TbAlertCircle, color: 'text-red-500' }
  };

  const handlePriorityChange = async (newPriority) => {
    try {
      const response = await axiosInstance.patch(`/tasks/${task.organization.slug}/${task.key}`, { 
        priority: newPriority 
      });
      
      if (onPriorityChange) {
        onPriorityChange(newPriority);
      }
      
      if (onTaskChange) {
        onTaskChange(response.data);
      }
    } catch (error) {
      console.error('Error updating priority:', error);
      toast({
        title: "Error",
        description: "Failed to update priority",
        variant: "destructive"
      });
    }
  };

  return (
    <div className={className} onClick={(e) => e.stopPropagation()}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className={`${!iconOnly ? 'h-7 px-1.5 -ml-1.5 flex items-center gap-2' : 'h-5 w-5 p-0 rounded-full bg-none hover:opacity-50'}`}>
              {React.createElement(priorities[task.priority].icon, { className: `w-5 h-5 ${priorities[task.priority].color}` })}
              {!iconOnly && priorities[task.priority].name}
            </Button>
            {showLabel}
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-[200px] p-0" side="top" align="start">
          <Command>
            <CommandInput placeholder="Change priority..." />
            <CommandList>
              <CommandEmpty>No priority found.</CommandEmpty>
              <CommandGroup>
                {Object.entries(priorities).map(([key, value]) => (
                  <CommandItem
                    key={key}
                    value={key}
                    onSelect={() => {
                      handlePriorityChange(key);
                      setOpen(false);
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" className="h-7 px-1.5 flex items-center gap-2">
                        {React.createElement(value.icon, { className: `w-4 h-4 ${value.color}` })}
                        {value.name}
                      </Button>
                    </div>
                    {task.priority === key && (
                      <Check className="ml-auto h-4 w-4" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
} 